/*
 * Buy-on-Amazon button controller
 * ---------------------------------------------------------------------------
 * Keeps every "Buy On Amazon" button pointed at the Amazon URL of the variant
 * the customer currently has selected — on the standard PDP (desktop AND mobile
 * layouts), the packages PDP, and the quick-shop modal.
 *
 * Why this file exists
 * --------------------
 * Each template used to carry its own copy of an inline sync script, and every
 * copy listened only for `input.pdp_selector-input` changes. That class exists
 * ONLY in the packages selector (snippets/packages-selector*.liquid). The
 * standard product template — which every cooker, cover, grate, lid, ash pan,
 * etc. uses — selects variants with completely different controls:
 *
 *   desktop  .variant-wrapper .variant-input input[type="radio"][name="id"]
 *            (snippets/variant-button.liquid)
 *   mobile   select[name="id"]
 *            (snippets/variant-dropdown.liquid)
 *
 * Neither matched, so no change event was ever observed and the button kept the
 * first variant's Amazon URL no matter which model the customer picked.
 *
 * This controller binds by the one thing all three selector styles share —
 * name="id" — via delegated listeners, so it works for radios, dropdowns and
 * hidden inputs alike, in whichever layout is currently on screen.
 *
 * It also normalizes the metafield value. Several products store the URL with
 * no scheme ("amazon.com/dp/B0GS3G4SSV"); the browser treats that as a relative
 * path and resolves it against the storefront, producing a 404 on our own site.
 * snippets/amazon-url.liquid applies the same rule to the server-rendered href.
 */
(function () {
  'use strict';

  var DATA_SELECTOR = '.js-amazon-urls, .js-amazon-qv-urls, #AmazonVariantUrls';
  var BTN_SELECTOR = '.js-amazon-buy-btn, .js-amazon-qv-btn';
  var ROOT_SELECTOR = '.quickview-footer, .page-content--product';
  var DESKTOP_SCOPE = '.pdp-main__info';
  var MOBILE_SCOPE = '.pdp-main__info--mobile';
  var SCHEME_RE = /^[a-z][a-z0-9+.-]*:\/\//i;

  /* Mirror of snippets/amazon-url.liquid. */
  function normalize(value) {
    if (!value) return '';
    var url = String(value).trim();
    if (!url) return '';
    if (url.indexOf('//') === 0) return 'https:' + url; // protocol-relative
    if (SCHEME_RE.test(url)) return url; // already absolute
    if (url.charAt(0) === '/') return url; // deliberate site-relative path
    return 'https://' + url; // bare host: amazon.com/dp/XXXX
  }

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function debounce(fn, wait) {
    var t;
    return function () { clearTimeout(t); t = setTimeout(fn, wait); };
  }

  /*
   * The container a button and its URL map share. A quick-shop modal nests its
   * own .quickview-footer inside a .page-content--product, so the footer must
   * win — that is what keeps two quick views on one collection page apart.
   */
  function rootOf(el) {
    if (!el || !el.closest) return document;
    return el.closest(ROOT_SELECTOR) || document;
  }

  function urlMap(root) {
    var el = root.querySelector ? root.querySelector(DATA_SELECTOR) : null;
    if (!el) return null;
    if (!el.amazonUrlMap) {
      var map;
      try { map = JSON.parse(el.textContent || '{}') || {}; } catch (e) { map = {}; }
      el.amazonUrlMap = map;
    }
    return el.amazonUrlMap;
  }

  /*
   * The PDP renders two independent forms (desktop + mobile) and hides one with
   * CSS. Read the selection from whichever is actually on screen, so switching
   * breakpoints or resizing does not leave the button on a stale variant.
   */
  function visibleScope(root) {
    if (!root.querySelector) return null;
    var scopes = [root.querySelector(MOBILE_SCOPE), root.querySelector(DESKTOP_SCOPE)];
    for (var i = 0; i < scopes.length; i++) {
      if (scopes[i] && scopes[i].offsetParent !== null) return scopes[i];
    }
    return null;
  }

  function idFrom(scope) {
    var checked = scope.querySelector('input[name="id"]:checked');
    if (checked && checked.value) return checked.value;
    var select = scope.querySelector('select[name="id"]');
    if (select && select.value) return select.value;
    var hidden = scope.querySelector('input[type="hidden"][name="id"]');
    if (hidden && hidden.value) return hidden.value;
    var legacy = scope.querySelector('.variant_id');
    if (legacy && legacy.value) return legacy.value;
    return null;
  }

  function selectedId(root) {
    if (!root.querySelector) return null;
    var visible = visibleScope(root);
    if (visible) {
      var id = idFrom(visible);
      if (id) return id;
    }
    return idFrom(root);
  }

  function apply(root, variantId) {
    var btns = root.querySelectorAll ? root.querySelectorAll(BTN_SELECTOR) : [];
    if (!btns.length) return;
    var map = urlMap(root);
    var url = map ? normalize(map[String(variantId)]) : '';
    for (var i = 0; i < btns.length; i++) {
      var btn = btns[i];
      if (url) {
        btn.setAttribute('href', url);
        btn.setAttribute('target', '_blank');
        btn.setAttribute('rel', 'noopener noreferrer');
        btn.removeAttribute('aria-disabled');
      } else {
        // No Amazon URL for this variant — inert rather than pointing somewhere wrong.
        btn.removeAttribute('href');
        btn.removeAttribute('target');
        btn.setAttribute('aria-disabled', 'true');
      }
    }
  }

  function sync(root) { apply(root, selectedId(root)); }

  function syncAll() {
    var roots = document.querySelectorAll(ROOT_SELECTOR);
    if (!roots.length) { sync(document); return; }
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      // Skip the outer product section when a quick view inside it owns the buttons.
      if (root.classList.contains('page-content--product') && root.querySelector('.quickview-footer')) continue;
      sync(root);
    }
  }

  ready(function init() {
    // Radios, dropdowns and the hidden Shop Pay master input all carry name="id".
    document.addEventListener('change', function (e) {
      var target = e.target;
      if (!target || target.name !== 'id') return;
      var root = rootOf(target);
      if (target.value) apply(root, target.value);
      else sync(root);
    });

    /*
     * Capture phase on purpose: custom-newJS.js handles "click the selected
     * package again to deselect" with a capture-phase listener that calls
     * stopPropagation(), which would otherwise keep this out of the bubble
     * phase. The timeout lets that handler restore the default variant first.
     */
    document.addEventListener('click', function (e) {
      var target = e.target;
      if (!target || !target.closest) return;
      if (!target.closest('label, .js-variant, .pdp_selector-block, .variant__button-label')) return;
      var root = rootOf(target);
      setTimeout(function () { sync(root); }, 0);
    }, true);

    // Crossing the mobile/desktop breakpoint swaps which form is authoritative.
    window.addEventListener('resize', debounce(syncAll, 150));

    // Apps (quick view, Rebuy) can inject product markup after load.
    var resyncSoon = debounce(syncAll, 100);
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if ((node.matches && (node.matches(BTN_SELECTOR) || node.matches(DATA_SELECTOR))) ||
              (node.querySelector && node.querySelector(BTN_SELECTOR))) {
            resyncSoon();
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    syncAll();
    window.addEventListener('load', syncAll);

    window.amazonBuyButton = { sync: syncAll, normalize: normalize, selectedId: selectedId };
  });
})();
